// StreamRecorder.tsx
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, } from "react";
import { saveChunk, createSegmentMeta, finishSegmentMeta } from "./db";
const StreamRecorder = forwardRef(({ label, stream, active, setPaused }, ref) => {
    const recorderRef = useRef(null);
    const segmentIdRef = useRef(null);
    const startedAtRef = useRef(null);
    const recordingRef = useRef(false);
    const lastStreamIdRef = useRef(null);
    const getMimeType = () => {
        var _a;
        const types = [
            "video/webm;codecs=vp9",
            "video/webm;codecs=vp8",
            "video/webm",
            "video/mp4",
        ];
        return (_a = types.find((t) => MediaRecorder.isTypeSupported(t))) !== null && _a !== void 0 ? _a : "";
    };
    const createSegmentId = () => {
        return `${label}_${Date.now()}_${crypto.randomUUID()}`;
    };
    const stopRecorder = useCallback(async () => {
        const rec = recorderRef.current;
        if (!rec) {
            return;
        }
        if (rec.state === "inactive") {
            recorderRef.current = null;
            return;
        }
        // IMPORTANT
        // clear refs BEFORE async stop
        recorderRef.current = null;
        const segmentId = segmentIdRef.current;
        segmentIdRef.current = null;
        startedAtRef.current = null;
        await new Promise((resolve) => {
            const original = rec.onstop;
            rec.onstop = async (e) => {
                original === null || original === void 0 ? void 0 : original.call(rec, e);
                if (segmentId) {
                    await finishSegmentMeta(segmentId, Date.now());
                }
                resolve();
            };
            rec.stop();
        });
    }, []);
    const startRecorder = useCallback(() => {
        if (!stream) {
            return;
        }
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            return;
        }
        const mimeType = getMimeType();
        const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        const segmentId = createSegmentId();
        segmentIdRef.current = segmentId;
        startedAtRef.current = Date.now();
        createSegmentMeta({
            id: segmentId,
            label,
            startedAt: startedAtRef.current,
        });
        rec.ondataavailable = async (e) => {
            if (!e.data.size) {
                return;
            }
            await saveChunk(e.data, segmentId);
        };
        rec.onerror = (e) => {
            console.error("[STREAM RECORDER ERROR]", JSON.stringify({
                label,
                segmentId,
                streamId: stream.id,
            }), e);
        };
        rec.onpause = () => { };
        rec.onresume = () => { };
        const track = stream.getVideoTracks()[0];
        if (track) {
            track.onmute = () => {
                if (rec.state === "recording") {
                    rec.pause();
                }
                setPaused(true);
            };
            track.onunmute = () => {
                if (rec.state === "paused") {
                    rec.resume();
                }
                setPaused(false);
            };
            track.onended = async () => {
                await stopRecorder();
            };
        }
        rec.start(1000);
        recorderRef.current = rec;
    }, [label, setPaused, stopRecorder, stream]);
    useEffect(() => {
        if (!active) {
            return;
        }
        if (!stream) {
            return;
        }
        if (lastStreamIdRef.current && lastStreamIdRef.current !== stream.id) {
        }
        lastStreamIdRef.current = stream.id;
        recordingRef.current = true;
        startRecorder();
        return () => {
            stopRecorder();
            const track = stream.getVideoTracks()[0];
            if (track) {
                track.onmute = null;
                track.onunmute = null;
                track.onended = null;
            }
        };
    }, [active, startRecorder, stopRecorder, stream === null || stream === void 0 ? void 0 : stream.id]);
    useImperativeHandle(ref, () => ({
        start() {
            recordingRef.current = true;
            if (stream) {
                startRecorder();
            }
        },
        async stop() {
            recordingRef.current = false;
            await stopRecorder();
        },
        pause() {
            const rec = recorderRef.current;
            if (rec && rec.state === "recording") {
                rec.pause();
            }
        },
        resume() {
            const rec = recorderRef.current;
            if (rec && rec.state === "paused") {
                rec.resume();
            }
        },
    }));
    return null;
});
export default StreamRecorder;
